import type { GridApi, IRowNode } from "ag-grid-community";
import { useMemo } from "react";

import {
  ASSERTION_DISPLAY_MODE,
  normalizeLLMAssertionValue,
  unwrapLLMAssertionEnvelope,
  type AssertionDisplayMode,
} from "../../assertions";
import { stringifyExactDisplayValue } from "../utils";

import type { SmartCell, SmartColumn } from "@/queries/smart-tables/types";
import { CellStatus, ColumnType, isTruncatedValueStub } from "@/queries/smart-tables/types";
import {
  hasErrorPayload,
  resolveCellDisplayState,
  stringifyDisplayValue,
} from "@/routes/smart-table/utils/cellDisplayState";
import { getCompositionSourceType } from "@/routes/smart-table/utils/compositionColumns";
import {
  isPlainTextEditableColumnType,
  isReadonlyAnchorColumnType,
  isStableDisplayColumnType,
  isTextColumnType,
} from "@/routes/smart-table/utils/columnTypeGuards";
import {
  CellContentType,
  classifyContent,
  classifyExecutionMetricContent,
  normalizeForClassification,
  stringifyRawCellContent,
} from "@/routes/smart-table/utils/contentType";
import { isExecutionMetadataChildColumn } from "@/routes/smart-table/utils/smartTableGroupedColumns";

const METADATA_OVERRIDABLE_CONTENT_TYPES = new Set([
  CellContentType.PLAIN_TEXT,
  CellContentType.JSON,
  CellContentType.MARKDOWN,
]);

/** Apply cell metadata hints on top of content classification. */
export const applyMetadataDisplayType = (
  detected: CellContentType,
  metadataDisplayType: string | undefined,
): CellContentType => {
  if (!METADATA_OVERRIDABLE_CONTENT_TYPES.has(detected)) {
    return detected;
  }
  if (metadataDisplayType === "object") {
    return CellContentType.JSON;
  }
  if (metadataDisplayType === "markdown" || metadataDisplayType === "html") {
    return CellContentType.MARKDOWN;
  }
  // JSON strings are stored as display_type "string". That should open as Text,
  // but it must not be treated as the content's own type when the user picks JSON.
  if (metadataDisplayType === "string" && detected === CellContentType.JSON) {
    return CellContentType.PLAIN_TEXT;
  }
  return detected;
};

/**
 * Null means "use the default view". Compare against that default, not the
 * raw classification — otherwise selecting JSON on a string-typed JSON payload
 * clears the override and the dialog stays on Text.
 */
export const nextDisplayTypeOverride = (
  selected: string,
  defaultDisplayType: string,
): string | null => (selected === defaultDisplayType ? null : selected);

interface UseDialogContentModelParams {
  api: GridApi | null | undefined;
  rowNode: IRowNode | undefined;
  rowData: Record<string, unknown> | undefined;
  column: SmartColumn | undefined;
  cell: SmartCell | undefined;
  hasFetchedFullCell: boolean;
  status: CellStatus;
  displayState: ReturnType<typeof resolveCellDisplayState>;
  displayColumnId: string;
  storageColumnId: string;
  compositionSource: string | undefined;
  dialogRendererColumnType: ColumnType | undefined;
  metricKey: string | null;
  displayTypeOverride: CellContentType | AssertionDisplayMode | null;
  canEditCell: (rowIndex: number | null | undefined, columnId: string | undefined) => boolean;
  rowIndex: number;
}

const getRowDataIndex = (rowData: Record<string, unknown> | undefined): number | null => {
  const value = rowData?._rowIndex;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const getAnchorDialogValue = ({
  cell,
  rowData,
  storageColumnId,
  displayColumnId,
}: {
  cell: SmartCell | undefined;
  rowData: Record<string, unknown> | undefined;
  storageColumnId: string;
  displayColumnId: string;
}): unknown =>
  cell?.value ??
  rowData?.[`_value_${storageColumnId}`] ??
  rowData?.[displayColumnId] ??
  cell?.display_value;

const DIALOG_MEDIA_MARKER_SCAN_LENGTH = 1_024;

export const resolveDialogClassificationInput = ({
  cellValue,
  residentFullValue,
  displayValue,
}: {
  cellValue: unknown;
  residentFullValue: unknown;
  displayValue: string | null;
}): unknown => {
  const normalizedValue = normalizeForClassification(residentFullValue ?? cellValue, displayValue);
  if (
    normalizedValue !== displayValue ||
    typeof displayValue !== "string" ||
    !displayValue.slice(0, DIALOG_MEDIA_MARKER_SCAN_LENGTH).includes('"output_media"')
  ) {
    return normalizedValue;
  }

  try {
    const parsedDisplayValue = JSON.parse(displayValue);
    return normalizeForClassification(parsedDisplayValue, displayValue);
  } catch {
    return normalizedValue;
  }
};

export const shouldUseFetchedAnchorDisplayValue = (
  hasFetchedFullCell: boolean,
  metricKey: string | null,
): boolean => hasFetchedFullCell && metricKey === null;

export function useDialogContentModel({
  api,
  rowNode,
  rowData,
  column,
  cell,
  hasFetchedFullCell,
  status,
  displayState,
  displayColumnId,
  storageColumnId,
  compositionSource,
  dialogRendererColumnType,
  metricKey,
  displayTypeOverride,
  canEditCell,
  rowIndex,
}: UseDialogContentModelParams) {
  const dialogCellValue = useMemo(() => {
    if (!displayColumnId) {
      return undefined;
    }

    const rowDataIndex = getRowDataIndex(rowData);
    if (rowDataIndex !== null && rowDataIndex !== rowIndex) {
      return undefined;
    }

    // Trace/Request cells show a short name in the grid; full view should show the
    // stored structured payload. Price/latency leaves share anchor storage via
    // parentColumnId — keep their own metric values.
    if (isReadonlyAnchorColumnType(column?.type) && !metricKey) {
      return getAnchorDialogValue({ cell, rowData, storageColumnId, displayColumnId });
    }

    // Metric leaves share their parent Trace/Request cell for storage. A full-cell
    // fetch therefore contains the anchor payload, not the clicked metric value.
    if (shouldUseFetchedAnchorDisplayValue(hasFetchedFullCell, metricKey)) {
      return cell?.display_value;
    }

    if (!cell && !metricKey) {
      const previewDisplayValue =
        rowData?.[`_display_${displayColumnId}`] ?? rowData?.[`_display_${storageColumnId}`];
      if (previewDisplayValue !== undefined) {
        return previewDisplayValue;
      }
    }

    if (api && rowNode) {
      try {
        const resolvedValue = api.getCellValue({
          rowNode,
          colKey: displayColumnId,
        });
        if (resolvedValue !== undefined) {
          return resolvedValue;
        }
      } catch {
        // fallback to row data below
      }
    }

    if (metricKey) {
      return (
        rowData?.[`_value_${displayColumnId}`] ??
        rowData?.[displayColumnId] ??
        rowData?.[`_display_${displayColumnId}`]
      );
    }

    return rowData?.[displayColumnId] ?? cell?.display_value;
  }, [
    api,
    cell,
    column?.type,
    displayColumnId,
    metricKey,
    hasFetchedFullCell,
    rowData,
    rowIndex,
    rowNode,
    storageColumnId,
  ]);

  const sourceColumnType =
    compositionSource !== undefined && column
      ? getCompositionSourceType(column, compositionSource)
      : undefined;

  const displayedValueHasError = hasErrorPayload(dialogCellValue);
  const hasDisplayedErrorState =
    displayedValueHasError || status === CellStatus.FAILED || displayState.isErrorState;
  // Only TEXT is "text-like" in the dialog. TRACE stays editable but renders its JSON payload.
  const hasExplicitTextDialogType =
    isTextColumnType(sourceColumnType) || isTextColumnType(dialogRendererColumnType);

  let fallbackDialogContentColumnType = column?.type;
  if (compositionSource !== undefined) {
    fallbackDialogContentColumnType = ColumnType.TEXT;
    if (displayedValueHasError) {
      fallbackDialogContentColumnType = ColumnType.COMPOSITION;
    }
  }

  const dialogContentColumnType = metricKey
    ? ColumnType.TEXT
    : (sourceColumnType ?? dialogRendererColumnType ?? fallbackDialogContentColumnType);
  const isTextLikeDialogContent =
    Boolean(metricKey) ||
    (!isReadonlyAnchorColumnType(dialogContentColumnType) &&
      (hasExplicitTextDialogType ||
        (!hasDisplayedErrorState &&
          (dialogContentColumnType === undefined || isTextColumnType(dialogContentColumnType)))));

  const hasDialogCellValue =
    dialogCellValue !== undefined &&
    dialogCellValue !== null &&
    (isTextLikeDialogContent || dialogCellValue !== "");

  const displayedCellState = useMemo(() => {
    if (isTextLikeDialogContent) {
      return resolveCellDisplayState({
        status,
        display_value: dialogCellValue,
        value: null,
      });
    }

    const displayedStatus = displayedValueHasError ? CellStatus.FAILED : status;

    const rowDataIndex = getRowDataIndex(rowData);
    const previewRawValue =
      !cell && (rowDataIndex === null || rowDataIndex === rowIndex)
        ? (rowData?.[`_value_${displayColumnId}`] ??
          rowData?.[`_value_${storageColumnId}`] ??
          rowData?.[displayColumnId] ??
          rowData?.[storageColumnId])
        : undefined;
    const residentPreviewValue = isTruncatedValueStub(previewRawValue)
      ? undefined
      : previewRawValue;

    return resolveCellDisplayState({
      status: displayedStatus,
      display_value: dialogCellValue,
      value: residentPreviewValue ?? dialogCellValue,
    });
  }, [
    cell,
    dialogCellValue,
    displayedValueHasError,
    isTextLikeDialogContent,
    rowData,
    rowIndex,
    status,
    displayColumnId,
    storageColumnId,
  ]);

  const effectiveDisplayState =
    hasDialogCellValue || compositionSource !== undefined ? displayedCellState : displayState;

  let effectiveStatus: CellStatus = status;
  if (!isTextLikeDialogContent && effectiveDisplayState.isErrorState) {
    effectiveStatus = CellStatus.FAILED;
  }

  const rowErrorMessage =
    rowData?.[`_error_${displayColumnId}`] ?? rowData?.[`_error_${storageColumnId}`];
  const errorMessage =
    isTextLikeDialogContent && !hasDisplayedErrorState
      ? undefined
      : (effectiveDisplayState.errorMessage ??
        displayState.errorMessage ??
        (typeof rowErrorMessage === "string" ? rowErrorMessage : undefined));

  let resolvedDialogValue: unknown = effectiveDisplayState.valueToDisplay;
  if (!isTextLikeDialogContent && effectiveDisplayState.isErrorState) {
    resolvedDialogValue = effectiveDisplayState.valueToDisplay;
  } else if (hasDialogCellValue) {
    resolvedDialogValue = dialogCellValue;
  }

  const rawResidentValue =
    cell?.value ?? rowData?.[`_value_${storageColumnId}`] ?? rowData?.[storageColumnId];
  const residentFullValue = isTruncatedValueStub(rawResidentValue) ? undefined : rawResidentValue;
  const rawDisplayColumnValue = rowData?.[`_value_${displayColumnId}`];
  const displayColumnFullValue = isTruncatedValueStub(rawDisplayColumnValue)
    ? undefined
    : rawDisplayColumnValue;

  let cellValue: unknown;
  if (metricKey) {
    cellValue =
      resolvedDialogValue ??
      rowData?.[`_value_${displayColumnId}`] ??
      rowData?.[displayColumnId] ??
      null;
  } else if (isTextLikeDialogContent) {
    cellValue = resolvedDialogValue ?? null;
  } else {
    cellValue =
      resolvedDialogValue ??
      residentFullValue ??
      displayColumnFullValue ??
      rowData?.[displayColumnId] ??
      null;
  }

  const preferExactDialogString =
    Boolean(metricKey) ||
    isTextLikeDialogContent ||
    isReadonlyAnchorColumnType(dialogContentColumnType);

  let displayValue: string | null = preferExactDialogString
    ? stringifyExactDisplayValue(resolvedDialogValue)
    : stringifyDisplayValue(
        effectiveDisplayState.isErrorState
          ? effectiveDisplayState.valueToDisplay
          : resolvedDialogValue,
      );

  if (displayValue === null && cellValue !== null) {
    displayValue = preferExactDialogString
      ? stringifyExactDisplayValue(cellValue)
      : stringifyDisplayValue(cellValue);
  }

  const rawStoredValue = metricKey
    ? (rowData?.[`_value_${displayColumnId}`] ?? rowData?.[displayColumnId] ?? cellValue)
    : (residentFullValue ?? displayColumnFullValue ?? rowData?.[`_display_${storageColumnId}`]);
  const normalizedAssertionRawValue =
    dialogContentColumnType === ColumnType.LLM_ASSERTION
      ? unwrapLLMAssertionEnvelope(rawStoredValue)
      : rawStoredValue;
  const classificationInput =
    dialogContentColumnType === ColumnType.LLM_ASSERTION
      ? (normalizedAssertionRawValue ?? normalizeForClassification(cellValue, displayValue))
      : resolveDialogClassificationInput({
          cellValue,
          residentFullValue,
          displayValue,
        });
  const assertionModel = useMemo(
    () =>
      dialogContentColumnType === ColumnType.LLM_ASSERTION
        ? normalizeLLMAssertionValue(dialogCellValue, rawStoredValue)
        : undefined,
    [dialogContentColumnType, dialogCellValue, rawStoredValue],
  );
  const shouldRenderComputationState =
    dialogContentColumnType !== undefined && !isStableDisplayColumnType(dialogContentColumnType);
  const shouldShowComputationStatus =
    shouldRenderComputationState || compositionSource !== undefined;
  const contentStatus = shouldShowComputationStatus ? effectiveStatus : CellStatus.STALE;
  const contentErrorMessage = shouldShowComputationStatus ? errorMessage : undefined;

  const content = useMemo(() => {
    const classifiedContent = classifyContent(
      classificationInput,
      displayValue,
      contentStatus,
      contentErrorMessage,
    );

    if (metricKey && classifiedContent.type !== CellContentType.ERROR) {
      return (
        classifyExecutionMetricContent(classificationInput, displayValue, metricKey) ??
        classifiedContent
      );
    }

    return classifiedContent;
  }, [classificationInput, displayValue, contentStatus, contentErrorMessage, metricKey]);

  const detectedType =
    content.type === CellContentType.EMPTY ? CellContentType.PLAIN_TEXT : content.type;
  const rowCellMetadata = rowData?.[`_metadata_${storageColumnId}`];
  const rowMetadataDisplayType =
    typeof rowCellMetadata === "object" &&
    rowCellMetadata !== null &&
    "display_type" in rowCellMetadata &&
    typeof rowCellMetadata.display_type === "string"
      ? rowCellMetadata.display_type
      : undefined;
  const metadataDisplayType = cell?.metadata?.display_type ?? rowMetadataDisplayType;
  const autoDetectedType = applyMetadataDisplayType(detectedType, metadataDisplayType);
  const defaultDisplayType =
    dialogContentColumnType === ColumnType.LLM_ASSERTION
      ? ASSERTION_DISPLAY_MODE
      : autoDetectedType;
  const displayType = displayTypeOverride ?? defaultDisplayType;

  const effectiveContent = useMemo(() => {
    if (displayType === ASSERTION_DISPLAY_MODE || displayType === content.type) {
      return content;
    }

    if (displayType === CellContentType.PLAIN_TEXT) {
      return {
        ...content,
        type: displayType,
        displayPreview: stringifyRawCellContent(content.rawValue),
      };
    }

    if (displayType === CellContentType.JSON) {
      return {
        ...content,
        type: displayType,
        parsedValue: content.rawValue,
      };
    }

    return { ...content, type: displayType };
  }, [content, displayType]);

  const canEdit =
    Boolean(cell) &&
    isPlainTextEditableColumnType(column?.type) &&
    !isExecutionMetadataChildColumn(column) &&
    canEditCell(rowIndex, storageColumnId);

  return {
    canEdit,
    assertionModel,
    content,
    dialogCellValue,
    defaultDisplayType,
    displayType,
    displayValue,
    effectiveContent,
    effectiveStatus,
    errorMessage,
    isTextLikeDialogContent,
    shouldShowComputationStatus,
  };
}
